import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import * as actionsNotificacao from 'store/notificacao/actions';
import * as selectorsNotificacao from 'store/notificacao/reducer';
import * as actionsRelatorioJob from 'store/relatorioJob/actions';
import * as selectorsRelatorioJob from 'store/relatorioJob/reducer';
import {
  NOTIFICACAO_POLL_MS,
  canNavigateNotificacao,
  getRelatorioJobFromNotificacao,
  isNotificacaoLida,
  isRelatorioGerando,
} from 'helpers/notificacao';
import NotificacaoItem from 'views/v2/Notificacoes/NotificacaoItem';

const PREVIEW_REFRESH_MS = 5000;

const NotificacaoBell = ({
  unreadCount,
  preview,
  downloadingId,
  fetchUnreadCount,
  fetchNotificacaoPreview,
  markNotificacaoRead,
  markAllNotificacoesRead,
  downloadRelatorioJob,
  history,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const empty = (preview || []).length === 0;
  const hasGerando = (preview || []).some((item) => isRelatorioGerando(item));

  useEffect(() => {
    fetchUnreadCount();
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchUnreadCount();
    };
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(fetchUnreadCount, NOTIFICACAO_POLL_MS);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(timer);
    };
  }, [fetchUnreadCount]);

  useEffect(() => {
    if (!open || !hasGerando) return undefined;
    const timer = window.setInterval(fetchNotificacaoPreview, PREVIEW_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [open, hasGerando, fetchNotificacaoPreview]);

  const handleClose = () => setAnchorEl(null);

  const handleOpenItem = (item) => {
    handleClose();
    if (!isNotificacaoLida(item)) markNotificacaoRead(item.id, true);
    if (canNavigateNotificacao(item)) {
      history.push(item.referenciaPath);
    }
  };

  const handleDownload = (item, job) => {
    downloadRelatorioJob(job);
    if (!isNotificacaoLida(item)) markNotificacaoRead(item.id, true);
  };

  return (
    <>
      <Tooltip title="Notificações">
        <IconButton
          aria-label="Notificações"
          color="inherit"
          onClick={(event) => {
            setAnchorEl(event.currentTarget);
            fetchNotificacaoPreview();
          }}
          sx={{
            color: (theme) => (
              theme.palette.mode === 'dark'
                ? theme.palette.common.white
                : theme.palette.text.primary
            ),
          }}
        >
          <Badge
            badgeContent={unreadCount}
            color="error"
            max={99}
          >
            <NotificationsOutlinedIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            mt: 1,
            width: 420,
            maxWidth: 'calc(100vw - 32px)',
            borderRadius: 2,
            p: 0,
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle2">Notificações</Typography>
          <Button
            size="small"
            disabled={!unreadCount}
            onClick={() => markAllNotificacoesRead()}
          >
            Ler todas
          </Button>
        </Box>
        {empty && (
          <Box sx={{ px: 2, py: 2 }}>
            <Typography
              variant="body2"
              color="text.secondary"
            >
              Nenhuma notificação.
            </Typography>
          </Box>
        )}
        {(preview || []).map((item) => (
          <MenuItem
            key={item.id}
            onClick={() => handleOpenItem(item)}
            sx={{
              alignItems: 'flex-start',
              py: 1.25,
              whiteSpace: 'normal',
              borderTop: (theme) => `1px solid ${theme.palette.divider}`,
            }}
          >
            <NotificacaoItem
              item={item}
              showMessage
              downloading={(() => {
                const job = getRelatorioJobFromNotificacao(item);
                return Boolean(job && downloadingId != null && String(downloadingId) === String(job.id));
              })()}
              onDownload={(job) => handleDownload(item, job)}
            />
          </MenuItem>
        ))}
        <Divider />
        <Box sx={{ py: 1, display: 'flex', justifyContent: 'center', gap: 1 }}>
          <Button
            size="small"
            onClick={() => {
              handleClose();
              history.push('/v2/notificacoes');
            }}
          >
            Ver todas
          </Button>
          <Button
            size="small"
            color="inherit"
            onClick={() => {
              handleClose();
              history.push('/v2/downloads');
            }}
          >
            Arquivos gerados
          </Button>
        </Box>
      </Menu>
    </>
  );
};

NotificacaoBell.propTypes = {
  unreadCount: PropTypes.number,
  preview: PropTypes.array,
  downloadingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fetchUnreadCount: PropTypes.func.isRequired,
  fetchNotificacaoPreview: PropTypes.func.isRequired,
  markNotificacaoRead: PropTypes.func.isRequired,
  markAllNotificacoesRead: PropTypes.func.isRequired,
  downloadRelatorioJob: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
};

NotificacaoBell.defaultProps = {
  unreadCount: 0,
  preview: [],
  downloadingId: null,
};

const mapStateToProps = (state) => ({
  unreadCount: selectorsNotificacao.getUnreadCount(state),
  preview: selectorsNotificacao.getNotificacaoPreview(state),
  downloadingId: selectorsRelatorioJob.getRelatorioJobDownloadingId(state),
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  ...actionsNotificacao,
  downloadRelatorioJob: actionsRelatorioJob.downloadRelatorioJob,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(NotificacaoBell));
